import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  SearchSubstationDto,
  SelectSubstationDataIn,
} from '../models/substation-template-instance.model';
import { CimSubstationForSave } from '../models/cim-substation.model';
import { ResponsePackage } from '../models/util.model';
import { SHARED_LIB_CONFIG, SharedLibConfig } from '../config/lib-config';
import { FeederBand } from '../models/ac-line-segment.model';

@Injectable({
  providedIn: 'root',
})
export class CimSubstationService {
  private pathApi: string;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  constructor(
    private http: HttpClient,
    @Inject(SHARED_LIB_CONFIG) private config: SharedLibConfig,
  ) {
    this.pathApi = this.config.pathApi + 'cim/CimSubstation/';
  }

  getAll(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}getAll`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getAllForSelect(dataIn: SearchSubstationDto): Observable<any> {
    return this.http.post(`${this.pathApi}getAllForSelect`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getAllForSelectMap(dataIn: SearchSubstationDto): Observable<any> {
    return this.http.post(`${this.pathApi}getAllForSelectMap`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getFirstSubstationWithPublishedSchema(): Observable<any> {
    return this.http.get(`${this.pathApi}getFirstSubstationWithPublishedSchema`, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getStats(): Observable<any> {
    return this.http.get(`${this.pathApi}getStats`, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getV3SubstationTemplateStats(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}getV3SubstationTemplateStats`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  delete(id: number, substationTemplateId: number, record: string): Observable<any> {
    return this.http.delete(`${this.pathApi}delete/${id}?substationTemplateId=${substationTemplateId}&record=${encodeURIComponent(record ?? '')}`, {
      headers: this.headers,
      observe: 'response',
    });
  }

  save(dataIn: CimSubstationForSave) {
    return this.http.post<ResponsePackage<any>>(
      `${this.pathApi}create`,
      dataIn,
      { headers: this.headers, observe: 'response' },
    );
  }

  getForSelect(dataIn: SelectSubstationDataIn): Observable<any> {
    return this.http.post(`${this.pathApi}getForSelect`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getOutgoingLines(substationId: number): Observable<any> {
    return this.http.get(`${this.pathApi}getOutgoingLines?id=${substationId}`, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getEquipmentForSubstation(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}getSubstationEquipment`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getEquipmentStatus(id: number) {
    return this.http.get<ResponsePackage<boolean>>(
      `${this.pathApi}getEquipmentStatus/${id}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }
  addIncommingAcLine(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}attachIncommingAcLine`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  generateTemplate(utilityId: number) {
    return this.http.get(
      `${this.pathApi}generateTemplate?utilityId=${utilityId}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }

  getEquipmentSchema(templateInstanceId: number): Observable<any> {
    return this.http.get(
      `${this.pathApi}getEquipmentSchema/${templateInstanceId}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }

  saveSchematicLayoutJson(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}saveSchematicLayoutJson`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getSchematicLayoutJsonById(templateInstanceId: number): Observable<any> {
    return this.http.get(
      `${this.pathApi}getSchematicLayoutJsonById/${templateInstanceId}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }

  downloadUntaggedSubstations(dataIn: any): Observable<any> {
    return this.http.post(
      `${this.pathApi}downloadUntaggedSubstations`,
      dataIn,
      {
        headers: this.headers,
      },
    );
  }

  uploadFile(data: any) {
    return this.http.post(`${this.pathApi}uploadFile`, data);
  }

  generateFeederReassignTemplate(utilityId: number) {
    return this.http.get(
      `${this.pathApi}generateBulkFeederReassignTemplate?utilityId=${utilityId}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }

  bulkFeederReassignment(data: any) {
    return this.http.post(`${this.pathApi}bulkFeederReassign`, data);
  }

  generateBulkSubstationUpdateTemplate(utilityId: number) {
    return this.http.get(
      `${this.pathApi}generateBulkSubstationUpdateTemplate?utilityId=${utilityId}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }

  bulkSubstationUpdate(data: any) {
    return this.http.post(`${this.pathApi}bulkSubstationUpdate`, data);
  }

  getHistory(substationId: number): Observable<any> {
    return this.http.get(`${this.pathApi}getHistory/${substationId}`, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getAllSubstationsCached(filter: any): Observable<any> {
    return this.http.post(`${this.pathApi}getAllSubstationsCached`, filter, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getFilteredBySubGeographical(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}getFilteredBySubGeographical`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  gisReview(data: { substationId: number; approved: boolean }): Observable<any> {
    return this.http.post(`${this.pathApi}gisReview`, data, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getMainFeederBand(id: number) {
    return this.http.get<number>(
      `${this.pathApi}getMainFeederBand/${id}`,
      {
        headers: this.headers,
        observe: 'response',
      },
    );
  }
}